import { findResourceTiming } from '@/app/pages/Diagnostics/diagnosticsUtils'
import { type ApiClientName, type ApiMetric, useDiagnosticsStore } from '@/stores/diagnosticsStore'
import axios, { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

interface DiagnosticsMeta {
	diagnosticsStart?: number
	diagnosticsId?: string
}

type ConfigWithDiagnostics = InternalAxiosRequestConfig & DiagnosticsMeta

const genId = (): string => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID()
	}
	return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Reconstruct the full request URL (baseURL + url + serialized params) so it
 * matches the URL recorded in PerformanceResourceTiming entries. Falls back to
 * manual join if axios.getUri throws (e.g. malformed config).
 */
const buildFullUrl = (config: InternalAxiosRequestConfig): string => {
	try {
		return axios.getUri(config)
	} catch {
		const base = config.baseURL || ''
		const url = config.url || ''
		if (!base || url.startsWith('http')) {
			return url
		}
		return `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`
	}
}

let bufferSizeBumped = false
const ensureResourceTimingBufferSize = (): void => {
	if (bufferSizeBumped) {
		return
	}
	bufferSizeBumped = true
	try {
		if (typeof performance !== 'undefined' && typeof performance.setResourceTimingBufferSize === 'function') {
			performance.setResourceTimingBufferSize(2000)
		}
	} catch {
		// Ignore — non-critical optimization.
	}
}

const recordMetric = (
	config: ConfigWithDiagnostics,
	clientName: ApiClientName,
	status: number | undefined,
	success: boolean,
	errorMessage: string | undefined,
): void => {
	const start = config.diagnosticsStart
	if (start === undefined) {
		return
	}
	const duration = performance.now() - start
	const startEpoch = performance.timeOrigin + start
	const fullUrl = buildFullUrl(config)
	const timing = findResourceTiming(fullUrl, startEpoch)

	const metric: ApiMetric = {
		id: config.diagnosticsId ?? genId(),
		url: fullUrl,
		method: (config.method ?? 'get').toUpperCase(),
		clientName,
		status,
		duration,
		ttfb: timing?.ttfb,
		download: timing?.download,
		dns: timing?.dns,
		connect: timing?.connect,
		transferBytes: timing?.transferBytes,
		timestamp: Date.now(),
		success,
		errorMessage,
	}
	useDiagnosticsStore.getState().addMetric(metric)
}

export const attachDiagnosticsInterceptor = (client: AxiosInstance, clientName: ApiClientName): void => {
	ensureResourceTimingBufferSize()

	client.interceptors.request.use(config => {
		const c = config as ConfigWithDiagnostics
		c.diagnosticsStart = performance.now()
		c.diagnosticsId = genId()
		return c
	})

	client.interceptors.response.use(
		(response: AxiosResponse) => {
			const config = response.config as ConfigWithDiagnostics
			recordMetric(config, clientName, response.status, true, undefined)
			return response
		},
		(error: AxiosError) => {
			const config = (error.config ?? {}) as ConfigWithDiagnostics
			const status = error.response?.status
			const success = false
			recordMetric(config, clientName, status, success, error.message)
			return Promise.reject(error)
		},
	)
}
